import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import {
  AppConfig,
  APP_CONFIG,
  HTTPTraceHeader,
  TraceService,
  SpanTypes,
} from '@sidkik/global';
import { Observable, catchError, tap, throwError } from 'rxjs';

export interface GoogleConfig {
  active: string;
  id: string;
  issue?: string;
  calendarSync: boolean;
}

export interface GoogleConnected {
  connected: boolean;
  issue: string;
}

export interface GoogleAPI {
  getAuthURI(trace?: HTTPTraceHeader): Observable<string>;
  getConnected(trace?: HTTPTraceHeader): Observable<GoogleConnected>;
  getConfig(trace?: HTTPTraceHeader): Observable<GoogleConfig>;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleService implements GoogleAPI {
  constructor(
    @Inject(APP_CONFIG) readonly tenantConfig: AppConfig,
    private readonly http: HttpClient,
    private readonly traceService: TraceService
  ) {}

  timeoutMs = 20000;

  retryConfig = {
    retries: 1,
    backoffMs: 1000,
    retryDelayMs: 1000,
    tooBusyRetries: 2,
    checksumRetries: 2,
  };

  private processHeaders(trace?: HTTPTraceHeader): HttpHeaders {
    let headers = new HttpHeaders({
      'ngsw-bypass': 'bypass',
      'Cache-Control': 'no-cache',
    });

    if (trace) {
      headers = new HttpHeaders({
        Traceparent: trace?.traceparent ?? '',
        Tracestate: trace?.tracestate ?? '',
        'ngsw-bypass': 'bypass',
        'Cache-Control': 'no-cache',
        'Access-Control-Expose-Headers':
          'Traceparent,TraceState,Orig-Tracestate,Orig-Traceparent,X-Tp', // expose the trace headers in response
      });
    }
    return headers;
  }

  getAuthURI(trace?: HTTPTraceHeader): Observable<string> {
    let internalTrace = false;
    if (!trace) {
      this.traceService.startSpan(SpanTypes.adminGoogleGetAuthUri);
      trace = this.traceService.getHTTPHeaderPropagators(
        SpanTypes.adminGoogleGetAuthUri
      );
      internalTrace = true;
    }
    return this.http
      .get<string>(`${this.tenantConfig.api.endpoint}/admin/google/auth`, {
        headers: this.processHeaders(trace),
      })
      .pipe(
        catchError((err: any) => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminGoogleGetAuthUri, err);
          return throwError(() => err);
        }),
        tap(() => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminGoogleGetAuthUri);
        })
      );
  }

  getConnected(trace?: HTTPTraceHeader): Observable<GoogleConnected> {
    let internalTrace = false;
    if (!trace) {
      this.traceService.startSpan(SpanTypes.adminGoogleGetConnected);
      trace = this.traceService.getHTTPHeaderPropagators(
        SpanTypes.adminGoogleGetConnected
      );
      internalTrace = true;
    }
    return this.http
      .get<GoogleConnected>(
        `${this.tenantConfig.api.endpoint}/admin/google/connected`,
        {
          headers: this.processHeaders(trace),
        }
      )
      .pipe(
        catchError((err: any) => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminGoogleGetConnected, err);
          return throwError(() => err);
        }),
        tap(() => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminGoogleGetConnected);
        })
      );
  }

  getConfig(trace?: HTTPTraceHeader): Observable<GoogleConfig> {
    let internalTrace = false;
    if (!trace) {
      this.traceService.startSpan(SpanTypes.adminGoogleGetConfig);
      trace = this.traceService.getHTTPHeaderPropagators(
        SpanTypes.adminGoogleGetConfig
      );
      internalTrace = true;
    }
    return this.http
      .get<GoogleConfig>(
        `${this.tenantConfig.api.endpoint}/admin/google/config`,
        {
          headers: this.processHeaders(trace),
        }
      )
      .pipe(
        catchError((err: any) => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminGoogleGetConfig, err);
          return throwError(() => err);
        }),
        tap(() => {
          internalTrace &&
            this.traceService.endSpan(SpanTypes.adminGoogleGetConfig);
        })
      );
  }
}
